import { Base } from '@studiometa/js-toolkit';

/**
 * Barre de filtres en pastilles de « Nos revendeurs » (chantier
 * store-locator-revendeurs, point 6, critère 3, scénarios 13-23). Remplace
 * `FilterPanel.js` (ancien panneau modal « Typologie de revendeur » seul, un
 * type à la fois) sur cette page : plusieurs puces cumulables, appliquées
 * immédiatement, sans modale à ouvrir.
 *
 * Composant top-level (comme `StoreResultsList`), monté sur la barre de puces
 * rendue par PHP (`archive-stores.php`, structure `.toolbar` de la maquette).
 * Ne calcule aucun filtrage lui-même : il lit l'état des puces (attributs
 * `data-filter-group`/`data-filter-value` déjà rendus par PHP) et diffuse le
 * résultat par l'évènement `store-locator-filters` (détail : voir
 * `StoreFilterLogic.js`, `StoreFilters`), écouté par `StoreResultsList`
 * (liste, toujours applicable même sans carte — invariant R9) et
 * `MapboxCluster` (marqueurs, quand la carte est chargée).
 */
export default class StoreFilterBar extends Base {
  static config = {
    name: 'StoreFilterBar',
    refs: ['chip[]', 'row'],
  };

  /** Position en cours d'un glisser à la souris sur la ligne de puces (défilement horizontal, sans barre visible). */
  dragState = null;

  mounted() {
    this.broadcast();
  }

  /**
   * Lit l'état actuel des puces et diffuse les filtres actifs. Appelé après
   * chaque clic de puce et après une réinitialisation.
   */
  broadcast() {
    const types = [];
    const services = [];
    let openNow = false;

    this.$refs.chip.forEach((chip) => {
      if (chip.getAttribute('aria-pressed') !== 'true') {
        return;
      }
      const group = chip.dataset.filterGroup;
      const value = chip.dataset.filterValue;
      if (group === 'type') {
        types.push(value);
      } else if (group === 'open') {
        openNow = true;
      } else if (group === 'service') {
        services.push(value);
      }
    });

    window.dispatchEvent(new CustomEvent('store-locator-filters', {
      detail: { types, openNow, services },
    }));
  }

  /**
   * Rediffuse l'état des puces à la demande de la carte, montée après le
   * chargement du service de carte : un filtre coché avant ce montage n'avait
   * été diffusé à personne (chantier store-locator-zoom-geoloc, revue ZF1).
   */
  onWindowStoreLocatorFiltersRequest() {
    this.broadcast();
  }

  /**
   * onChipClick
   * @param {PointerEvent} event
   * @param {number} index
   */
  onChipClick(event, index) {
    const chip = this.$refs.chip[index];
    const isActive = chip.getAttribute('aria-pressed') === 'true';
    chip.setAttribute('aria-pressed', isActive ? 'false' : 'true');
    this.broadcast();
  }

  /**
   * « Réinitialiser les filtres », déclenché depuis l'état vide de la liste
   * (`StoreResultsList`, hors de ce composant — le bouton vit dans `#list`,
   * qui n'a pas connaissance de cette barre) via l'évènement global
   * `store-locator-filters-reset`.
   */
  onWindowStoreLocatorFiltersReset() {
    this.$refs.chip.forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
    this.broadcast();
  }

  /**
   * Défilement horizontal de la ligne de puces sans barre visible : la
   * molette verticale (la seule disponible sur la plupart des souris) fait
   * défiler horizontalement (maquette : « défilement horizontal sans barre »).
   * @param {WheelEvent} event
   */
  onRowWheel(event) {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
      // Défilement déjà horizontal (trackpad) : laisser le comportement natif.
      return;
    }
    event.preventDefault();
    this.$refs.row.scrollLeft += event.deltaY;
  }

  /**
   * Glisser à la souris pour faire défiler la ligne de puces (maquette :
   * « glisser souris »). Le clic simple sur une puce reste un clic normal
   * (aucun seuil de déplacement franchi = pas de `preventDefault`, donc le
   * `click` natif de la puce se déclenche normalement).
   * @param {PointerEvent} event
   */
  onRowMousedown(event) {
    this.dragState = {
      startX: event.clientX,
      scrollLeft: this.$refs.row.scrollLeft,
      moved: false,
    };
  }

  /**
   * @param {PointerEvent} event
   */
  onDocumentMousemove(event) {
    if (!this.dragState) {
      return;
    }
    const delta = event.clientX - this.dragState.startX;
    if (Math.abs(delta) > 3) {
      this.dragState.moved = true;
      this.$refs.row.scrollLeft = this.dragState.scrollLeft - delta;
    }
  }

  onDocumentMouseup() {
    this.dragState = null;
  }
}
